export class Validation {
  /**
   * コンストラクタ：必要なDOM要素を取得・初期化
   */
  constructor() {
    this.DOM = {};

    this.DOM.formContainer = document.querySelector('[data-validation]');
    if (!this.DOM.formContainer) return;

    this.DOM.form = this.DOM.formContainer.closest('form');
    if (!this.DOM.form) return;

    // ヘッダー
    this.DOM.header = document.querySelector('header');

    // 確認画面用メッセージ要素
    this.DOM.confirmMessage = document.querySelector('[data-confirm-message]');

    // プライバシー関連
    this.DOM.privacyLabel = document.querySelector('[data-privacypolicy]');
    this.DOM.dummyPrivacy = document.querySelector('[data-privacypolicy] input[type="checkbox"]');
    this.DOM.hiddenPrivacy = document.querySelector('[data-privacypolicy-hidden] input[type="checkbox"]');

    // ボタン類
    this.DOM.customSubmitBtn = this.DOM.form.querySelector('[data-submit]');
    this.DOM.customSubmitText = this.DOM.form.querySelector('[data-submit-text]');
    this.DOM.customEditBtn = this.DOM.form.querySelector('[data-edit]');

    // CF7本来の送信ボタン
    this.DOM.originalSubmitBtn = this.DOM.form.querySelector('[data-submit-hidden] input[type="submit"]');

    // 自動住所入力ボタン
    this.DOM.autoAddressBtn = document.querySelector('[data-autoaddress-trigger]');

    // 入力要素一覧
    this.DOM.allInputs = this.DOM.form.querySelectorAll('input, select, textarea');

    this.CLASS = {
      HAS_ERROR: 'has-error',
      ERROR_MESSAGE: 'c-error',
      CONFIRMED: 'is-confirmed', // 確認モード状態用クラス
      IS_HIDDEN: 'is-hidden', // 非表示用クラス
      IS_SHOW: 'is-show', // メッセージ表示用クラス
      IS_SUBMITTING: 'is-submitting' // 送信中用クラス
    };

    this.ID = {
      AGE: 'age',
      ZIP: 'zip',
      DISPLAY_PRIVACYPOLICY: 'display_privacypolicy',
    }

    this.DATA_ATTR = {
      ERROR_MESSAGE: 'data-validation-error-message',
      PRIVACYPOLICY_SET: 'data-privacypolicy-set',
      INPUT_ITEM: 'data-validation-item',
    };

    // モード管理（false: 入力モード / true: 確認モード）
    this.isConfirmed = false;
    // 送信中フラグ（ダブルクリック防止用）
    this.isSubmitting = false;
    // 送信完了フラグ（再送信防止用）
    this.isSent = false;

    this.init();
  }

  init() {
    this.setupNumericKeyboards(); // 数字キーボード化の追加
    this.bindPrivacySync();
    this.bindSubmitEvent();
    this.bindEditEvent();
    this.bindRealtimeValidation();
    this.bindCF7Events(); // CF7送信ステータスイベントの登録
  }

  /**
   * 年齢や郵便番号などの [text] 項目に数字キーボード属性を付与する
   */
  setupNumericKeyboards() {
    const numericInputIds = [`#${this.ID.AGE}`, `#${this.ID.ZIP}`]; // 数字キーボードを出したい要素のID

    numericInputIds.forEach((id) => {
      const input = this.DOM.form ? this.DOM.form.querySelector(id) : document.querySelector(id);
      if (input) {
        // スマホで数字キーボードを強制表示させる属性
        input.setAttribute('inputmode', 'numeric');
        // 必要に応じて半角数字限定のパターン（任意）
        // input.setAttribute('pattern', '[0-9]*');
      }
    });
  }

  /**
   * 1. 個人情報同意チェックボックスの同期＆バリデーション
   */
  bindPrivacySync() {
    if (!this.DOM.dummyPrivacy || !this.DOM.hiddenPrivacy) return;

    this.DOM.dummyPrivacy.addEventListener('change', () => {
      if (this.isSent) return;
      this.DOM.hiddenPrivacy.checked = this.DOM.dummyPrivacy.checked;
      this.validatePrivacyCheckbox();
    });
  }

  /**
   * プライバシープリシーチェックボックス単体のバリデーション
   */
  validatePrivacyCheckbox() {
    if (!this.DOM.dummyPrivacy) return true;

    const parent = this.DOM.dummyPrivacy.closest(`[${this.DATA_ATTR.PRIVACYPOLICY_SET}]`);
    if (!parent) return true;

    if (this.DOM.dummyPrivacy.checked) {
      if (this.DOM.privacyLabel) {
        this.DOM.privacyLabel.classList.remove(this.CLASS.HAS_ERROR);
      }
      this.clearError(parent, this.DOM.dummyPrivacy);
      return true;
    } else {
      if (this.DOM.privacyLabel) {
        this.DOM.privacyLabel.classList.add(this.CLASS.HAS_ERROR);
      }
      this.showError(parent, this.DOM.dummyPrivacy, '同意が必要です');
      return false;
    }
  }

  /**
   * 2. 「確認 / 送信」ボタンの処理
   */
  bindSubmitEvent() {
    if (!this.DOM.customSubmitBtn || !this.DOM.originalSubmitBtn) return;

    this.DOM.customSubmitBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // すでに送信完了、または送信中の場合は処理をブロック
      if (this.isSent || this.isSubmitting) return;

      if (!this.isConfirmed) {
        // 【確認モードでない場合】バリデーションを実行し、問題なければ確認モードへ
        const isValid = this.validateAllFields();

        if (isValid) {
          this.switchToConfirmMode();
        } else {
          this.scrollToFirstError();
        }
      } else {
        // 【確認モードの場合】送信処理開始

        // ① 送信中フラグを立て、重複クリック・連打を禁止する
        this.isSubmitting = true;
        this.DOM.customSubmitBtn.disabled = true;
        this.DOM.customSubmitBtn.classList.add(this.CLASS.IS_SUBMITTING);

        if (this.DOM.customEditBtn) {
          this.DOM.customEditBtn.disabled = true;
        }

        if (this.DOM.customSubmitText) {
          this.DOM.customSubmitText.textContent = '送信中...';
        }

        // ② CF7にフォーム値を渡すため、一度 disabled を解除
        this.setInputsDisabled(false);

        // ③ わずかに遅延させてCF7の送信ボタンをクリック
        setTimeout(() => {
          this.DOM.originalSubmitBtn.click();
        }, 500);
      }
    });
  }

  /**
   * 3. 「編集する」ボタンの処理
   */
  bindEditEvent() {
    if (!this.DOM.customEditBtn) return;

    this.DOM.customEditBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // 送信中または送信完了している場合は操作不可
      if (this.isSubmitting || this.isSent) return;

      this.switchToEditMode();
    });
  }

  /**
   * ★追加：Contact Form 7 のイベントフック（送信成功/失敗時のガード）
   */
  bindCF7Events() {
    // 送信成功時 (再送信防止)
    document.addEventListener('wpcf7mailsent', () => {
      this.isSent = true;
      this.isSubmitting = false;

      // フォーム全体と全入力項目をロック
      this.setInputsDisabled(true);

      // 送信ボタンと編集ボタンを無効化
      if (this.DOM.customSubmitBtn) {
        this.DOM.customSubmitBtn.disabled = true;
        this.DOM.customSubmitBtn.classList.remove(this.CLASS.IS_SUBMITTING);
      }
      if (this.DOM.customEditBtn) {
        this.DOM.customEditBtn.disabled = true;
        this.DOM.customEditBtn.classList.add(this.CLASS.IS_HIDDEN);
      }

      // ボタン表示テキストを変更
      if (this.DOM.customSubmitText) {
        this.DOM.customSubmitText.textContent = '送信が完了しました';
      }

      // 確認メッセージを隠す
      if (this.DOM.confirmMessage) {
        this.DOM.confirmMessage.classList.remove(this.CLASS.IS_SHOW);
      }
    }, false);

    // 送信失敗時 (通信エラー等で失敗した場合の復元処理)
    const handleFailure = () => {
      if (this.isSent) return;

      this.isSubmitting = false;
      this.DOM.customSubmitBtn.disabled = false;
      this.DOM.customSubmitBtn.classList.remove(this.CLASS.IS_SUBMITTING);

      if (this.DOM.customEditBtn) {
        this.DOM.customEditBtn.disabled = false;
      }

      if (this.DOM.customSubmitText) {
        this.DOM.customSubmitText.textContent = '送信する';
      }

      // 再度入力内容を確認できるように確認モード状態（disabled）に戻す
      this.setInputsDisabled(true);
    };

    document.addEventListener('wpcf7mailfailed', handleFailure, false);
    document.addEventListener('wpcf7invalid', handleFailure, false);
  }

  /**
   * ② 確認モードへ切り替え
   */
  switchToConfirmMode() {
    this.isConfirmed = true;

    // 全ての入力項目およびプライバシーポリシーエリアを無効化
    this.setInputsDisabled(true);

    // 確認メッセージを表示
    if (this.DOM.confirmMessage) {
      this.DOM.confirmMessage.classList.add(this.CLASS.IS_SHOW);
    }

    // ボタン表示を「送信する」に変更
    if (this.DOM.customSubmitText) {
      this.DOM.customSubmitText.textContent = '送信する';
    }
    if (this.DOM.customSubmitBtn) {
      this.DOM.customSubmitBtn.classList.add(this.CLASS.CONFIRMED);
      this.DOM.customSubmitBtn.disabled = false;
    }

    // 「編集する」ボタンを表示
    if (this.DOM.customEditBtn) {
      this.DOM.customEditBtn.classList.remove(this.CLASS.IS_HIDDEN);
      this.DOM.customEditBtn.disabled = false;
    }

    // フォーム上部へスクロール
    this.scrollToFormTop();
  }

  /**
   * ⑤ 編集モードへ切り替え
   */
  switchToEditMode() {
    this.isConfirmed = false;

    // 全ての入力項目およびプライバシーポリシーエリアを有効化
    this.setInputsDisabled(false);

    // 確認メッセージを非表示化
    if (this.DOM.confirmMessage) {
      this.DOM.confirmMessage.classList.remove(this.CLASS.IS_SHOW);
    }

    // ボタン表示を「入力内容を確認する」に戻す
    if (this.DOM.customSubmitText) {
      this.DOM.customSubmitText.textContent = '入力内容を確認する';
    }
    if (this.DOM.customSubmitBtn) {
      this.DOM.customSubmitBtn.classList.remove(this.CLASS.CONFIRMED);
      this.DOM.customSubmitBtn.disabled = false;
    }

    // 「編集する」ボタンを非表示化
    if (this.DOM.customEditBtn) {
      this.DOM.customEditBtn.classList.add(this.CLASS.IS_HIDDEN);
    }

    // フォーム上部へスクロール
    this.scrollToFormTop();
  }

  /**
   * 全入力要素およびプライバシーポリシー項目の disabled を切り替え
   */
  setInputsDisabled(disabled) {
    if (this.DOM.allInputs) {
      this.DOM.allInputs.forEach((input) => {
        if (input.type === 'hidden' || input.type === 'submit') return;
        input.disabled = disabled;
      });
    }

    if (this.DOM.privacyLabel) {
      const privacyInputs = this.DOM.privacyLabel.querySelectorAll('input, select, textarea');
      privacyInputs.forEach((input) => {
        input.disabled = disabled;
      });
    }

    if (this.DOM.dummyPrivacy) {
      this.DOM.dummyPrivacy.disabled = disabled;
    }

    if (this.DOM.autoAddressBtn) {
      this.DOM.autoAddressBtn.disabled = disabled;
    }
  }

  /**
   * フォーム上部へスクロールする処理
   */
  scrollToFormTop() {
    const headerElement = this.DOM.header;
    const headerHeight = headerElement ? headerElement.offsetHeight : 0;
    const extraBuffer = 20;
    const totalOffset = headerHeight + extraBuffer;

    const elementPosition = this.DOM.formContainer.getBoundingClientRect().top;
    const offsetPosition = window.pageYOffset + elementPosition - totalOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  /**
   * 4. リアルタイムバリデーションのイベント登録
   */
  bindRealtimeValidation() {
    if (!this.DOM.allInputs) return;

    this.DOM.allInputs.forEach((input) => {
      if (input.id === this.ID.DISPLAY_PRIVACYPOLICY) return;

      input.addEventListener('blur', () => {
        if (!this.isConfirmed && !this.isSent) {
          this.validateSingleField(input);
        }
      });

      input.addEventListener('input', () => {
        if (!this.isConfirmed && !this.isSent && input.classList.contains(this.CLASS.HAS_ERROR)) {
          this.validateSingleField(input);
        }
      });

      input.addEventListener('change', () => {
        if (!this.isConfirmed && !this.isSent) {
          this.validateSingleField(input);
        }
      });
    });
  }

  validateSingleField(input) {
    if (input.type === 'submit' || input.type === 'button') return true;

    const parent = input.closest(`[${this.DATA_ATTR.INPUT_ITEM}]`) || input.closest(`[${this.DATA_ATTR.PRIVACYPOLICY_SET}]`);
    if (!parent) return true;

    let errorMessage = '';
    const val = input.value.trim();

    if (['user-name', 'user-kana', 'city', 'street'].includes(input.name) || input.id === 'name' || input.id === 'kana') {
      if (val === '') errorMessage = '必須項目を入力してください。';
    }

    if ((input.id === 'kana' || input.name === 'user-kana') && val !== '') {
      if (!/^[ァ-ヶー\s]+$/.test(val)) errorMessage = '全角カタカナで入力してください。';
    }

    if (input.id === 'zip' || input.name === 'zip') {
      if (val === '') errorMessage = '郵便番号を入力してください。';
      else if (!/^\d{3}-?\d{4}$/.test(val)) errorMessage = '正しい郵便番号の形式で入力してください。';
    }

    if (input.name === 'prefecture') {
      if (val === '' || val === '選択してください') errorMessage = '都道府県を選択してください。';
    }

    if (input.id === 'phone' || input.name === 'phone') {
      if (val === '') errorMessage = '電話番号を入力してください。';
      else if (!/^\d{10,11}$/.test(val.replace(/-/g, ''))) errorMessage = '正しい電話番号を入力してください。';
    }

    if (input.id === 'e-mail' || input.name === 'e-mail') {
      if (val === '') errorMessage = 'メールアドレスを入力してください。';
      else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)) errorMessage = '正しいメールアドレスの形式で入力してください。';
    }

    if (input.id === 'e-mail_confirm' || input.name === 'e-mail_confirm') {
      const originalEmail = this.DOM.form.querySelector('#e-mail')?.value.trim();
      if (val === '') errorMessage = '確認用メールアドレスを入力してください。';
      else if (val !== originalEmail) errorMessage = 'メールアドレスが一致しません。';
    }

    if (errorMessage) {
      this.showError(parent, input, errorMessage);
      return false;
    } else {
      this.clearError(parent, input);
      return true;
    }
  }

  /**
   * フォーム全体のバリデーション実行
   */
  validateAllFields() {
    let isValid = true;

    const requiredInputs = this.DOM.form.querySelectorAll(`[${this.DATA_ATTR.INPUT_ITEM}] input, [${this.DATA_ATTR.INPUT_ITEM}] select, [${this.DATA_ATTR.INPUT_ITEM}] textarea`);
    requiredInputs.forEach((input) => {
      if (!this.validateSingleField(input)) {
        isValid = false;
      }
    });

    const radioGroup = this.DOM.form.querySelectorAll('input[name="employment-status"]');
    if (radioGroup.length > 0) {
      const isRadioChecked = Array.from(radioGroup).some((r) => r.checked);
      const radioParent = radioGroup[0].closest(`[${this.DATA_ATTR.INPUT_ITEM}]`);

      if (!isRadioChecked) {
        this.showError(radioParent, radioGroup[0], '就業状況を選択してください。');
        isValid = false;
      } else {
        this.clearError(radioParent, radioGroup[0]);
      }
    }

    if (!this.validatePrivacyCheckbox()) {
      isValid = false;
    }

    return isValid;
  }

  scrollToFirstError() {
    const firstError = this.DOM.form.querySelector(`.${this.CLASS.HAS_ERROR}, [${this.DATA_ATTR.ERROR_MESSAGE}]`);
    if (!firstError) return;

    const firstErrorItem = firstError.closest(`[${this.DATA_ATTR.INPUT_ITEM}]`) || firstError.closest(`[${this.DATA_ATTR.PRIVACYPOLICY_SET}]`);
    const targetElement = firstErrorItem || firstError;

    const headerElement = this.DOM.header;
    const headerHeight = headerElement ? headerElement.offsetHeight : 0;
    const extraBuffer = 20;
    const totalOffset = headerHeight + extraBuffer;

    const elementPosition = targetElement.getBoundingClientRect().top;
    const offsetPosition = window.pageYOffset + elementPosition - totalOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  showError(parent, input, message) {
    if (input) input.classList.add(this.CLASS.HAS_ERROR);

    let errorEl = parent.querySelector(`[${this.DATA_ATTR.ERROR_MESSAGE}]`);

    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.className = this.CLASS.ERROR_MESSAGE;
      errorEl.setAttribute(this.DATA_ATTR.ERROR_MESSAGE, '');
      parent.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }

  clearError(parent, input) {
    if (input) input.classList.remove(this.CLASS.HAS_ERROR);

    const errorEl = parent.querySelector(`[${this.DATA_ATTR.ERROR_MESSAGE}]`);
    if (errorEl) {
      errorEl.remove();
    }
  }
}
